import { h, mount } from "@/lib/dom";
import { button, loading } from "@/components/ui";
import { aiStatus } from "@/services/ai.service";

/**
 * „Warum geht die KI nicht?" – eine Karte, die den Nutzer nicht raten lässt.
 *
 * Der frühere Text „Keine Verbindung zum Server. Ist PocketBase erreichbar?"
 * war fast immer falsch: der Server lief, aber entweder fehlte der Hook, der
 * GROQ_API_KEY war nicht gesetzt oder die Antwort dauerte zu lange. Diese
 * Karte fragt genau das beim Server nach und nennt den nächsten Schritt.
 */
export function aiTroubleCard(message?: string): HTMLElement {
  const slot = h("div", { class: "ai-diag-slot" });

  const runBtn = button({
    label: "KI-Verbindung prüfen",
    icon: "refresh-cw",
    variant: "ghost",
    size: "sm",
    onClick: async () => {
      runBtn.disabled = true;
      mount(slot, loading());
      const s = await aiStatus();
      mount(
        slot,
        h(
          "div",
          { class: "ai-diag " + (s.ok ? "ok" : "err") },
          h("div", { class: "ai-diag-head" }, s.ok ? "✓ KI erreichbar" : "✗ KI nicht erreichbar"),
          h("div", { class: "ai-diag-msg" }, s.message),
          s.ok ? null : h("ul", { class: "ai-diag-steps" }, ...fixSteps(s.message).map((t) => h("li", null, t)))
        )
      );
      runBtn.disabled = false;
    },
  });

  return h(
    "div",
    { class: "card card-pad lt-ai-hint" },
    h("p", null, message ?? "Die KI antwortet gerade nicht."),
    h("div", { class: "row", style: "margin-top:10px" }, runBtn),
    slot
  );
}

/** Aus der Serverantwort die konkreten Handgriffe ableiten. */
function fixSteps(message: string): string[] {
  const m = message.toLowerCase();
  if (m.includes("openrouter_api_key")) {
    return [
      "Auf https://openrouter.ai/keys einen Schlüssel erzeugen (kostenlos, ohne Kreditkarte).",
      "Beim PocketBase-Hoster die Env-Variable OPENROUTER_API_KEY auf diesen Wert setzen.",
      "PocketBase neu starten – Hooks werden beim Start geladen.",
    ];
  }
  if (m.includes("groq_api_key")) {
    return [
      "Auf https://console.groq.com/keys einen Schlüssel erzeugen (kostenlos, ohne Kreditkarte) – für die Spracherkennung.",
      "Beim PocketBase-Hoster die Env-Variable GROQ_API_KEY auf diesen Wert setzen.",
      "PocketBase neu starten – Hooks werden beim Start geladen.",
    ];
  }
  if (m.includes("proxy fehlt") || m.includes("hooks.pb.js")) {
    return [
      "pocketbase/pb_hooks/hooks.pb.js aus diesem Projekt auf den Server nach pb_hooks/ hochladen.",
      "PocketBase neu starten.",
    ];
  }
  if (m.includes("nicht eingeloggt")) return ["Abmelden und neu einloggen."];
  if (m.includes("nicht erreichbar")) {
    return [
      "Internetverbindung prüfen.",
      "URL in src/config.ts gegen die Adresse im PocketBase-Adminbereich abgleichen.",
    ];
  }
  if (m.includes("lehnt den schlüssel ab")) {
    return ["OPENROUTER_API_KEY ist abgelaufen oder falsch – auf openrouter.ai/keys neu erzeugen und setzen."];
  }
  if (m.includes("tageslimit")) {
    return ["Server-Tageslimit erreicht – morgen wieder verfügbar, oder OPENROUTER_DAILY_LIMIT beim Hoster erhöhen."];
  }
  return ["In ein paar Minuten noch einmal versuchen – OpenRouter drosselt bei Andrang."];
}
